import React, { useEffect } from "react";
import { IonApp, IonSplitPane, IonRouterOutlet } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Route, Redirect, Switch } from "react-router-dom";
import { AuthContext, useAuthInit } from "./Auth";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ToastContainer } from "react-toastify";

// Import all your pages
import LoginPage from "./pages/LoginPage";
import LoginSchool from "./pages/login_school";
import LoginCie from "./pages/login_cie";
import LoginAdmin from "./pages/LoginAdmin";
import LoginCheckResult from "./pages/login_check_result";
import SchoolDashboard from "./pages/school/SchoolDashboard";
import SchoolRegistration from "./pages/school/SchoolRegistration";
import RegisteredStudents from "./pages/school/RegisteredStudents";
import ValidationListPage from "./pages/school/ValidationListPage";
import CieDashboard from "./pages/cie/CieDashboard";
import CieValidationListPage from "./pages/cie/CieValidationListPage";
import StudentList from "./pages/admin/StudentList";
import LGAStudentDatabase from "./pages/admin/LGAStudentDatabase";
import AddResult from "./pages/admin/AddResult";
import SchoolManagement from "./pages/admin/SchoolManagement";
import ManagementConsole from "./pages/admin/ManagementConsole";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminSettings from "./pages/admin/AdminSettings";
import ValidationListPage_online from "./pages/school/ValidationListPage_online";
import PhotobookPage from "./pages/admin/PhotobookPage";
import ActivityStatsPage from "./pages/admin/ActivityStatsPage";
import ResultDisplay from "./pages/ResultDisplay";

// CSS imports
import "@ionic/react/css/core.css";
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";
import "./theme/variables.css";

// Protected Route wrapper component for v5
const ProtectedRoute = ({ component: Component, redirectTo = "/login", ...rest }) => {
  const token = localStorage.getItem('userToken'); // or your token key
  
  return (
    <Route
      {...rest}
      render={(props) => {
        if (!token) {
          return <Redirect to={{ pathname: redirectTo }} />;
        }
        return <Component {...props} />;
      }}
    />
  );
};

// Public Route wrapper (redirects to dashboard if already logged in)
const PublicRoute = ({ component: Component, redirectTo = "/school_dashboard", ...rest }) => {
  const token = localStorage.getItem('userToken');
  
  return (
    <Route
      {...rest}
      render={(props) => {
        if (token) {
          // Determine which dashboard to redirect to based on user role
          // You'll need to implement this logic based on your user data
          return <Redirect to={{ pathname: redirectTo }} />;
        }
        return <Component {...props} />;
      }}
    />
  );
};

const App: React.FC = () => {
  const authInitState = useAuthInit();

  return (
    <IonApp>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AuthContext.Provider value={{ isLoggedIn: authInitState.isLoggedIn }}>
          <IonReactRouter>
            {authInitState.loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <p>Loading...</p>
              </div>
            ) : (
              <IonSplitPane contentId="main">
                <IonRouterOutlet id="main">
                  <Switch>
                    {/* Public Routes */}
                    <Route exact path="/" render={() => <Redirect to="/login" />} />
                    
                    <PublicRoute 
                      exact 
                      path="/login" 
                      component={LoginPage} 
                      redirectTo="/school_dashboard" // Change this based on user role
                    />
                    
                    <PublicRoute 
                      exact 
                      path="/login_school" 
                      component={LoginSchool} 
                      redirectTo="/school_dashboard"
                    />
                    
                    <PublicRoute 
                      exact 
                      path="/login_cie" 
                      component={LoginCie} 
                      redirectTo="/cie_dashboard"
                    />
                    
                    <PublicRoute 
                      exact 
                      path="/login_admin" 
                      component={LoginAdmin} 
                      redirectTo="/admin_dashboard"
                    />
                    
                    <PublicRoute 
                      exact 
                      path="/login_check_result" 
                      component={LoginCheckResult} 
                      redirectTo="/results"
                    />

                    <Route 
                      exact 
                      path="/result-display" 
                      component={ResultDisplay} 
                    />

                    {/* Protected Routes */}
                    <ProtectedRoute 
                      exact 
                      path="/school_dashboard" 
                      component={SchoolDashboard} 
                      redirectTo="/login_school"
                    />
                    
                    <ProtectedRoute 
                      exact 

                      path="/school-registration" 
                      component={SchoolRegistration} 
                      redirectTo="/login_school"
                    />
                    
                    <ProtectedRoute 
                      exact 
                      path="/registered-students" 
                      component={RegisteredStudents} 
                      redirectTo="/login_school"
                    />
                    
                    <ProtectedRoute 
                      exact 
                      path="/validation-list" 
                      component={ValidationListPage} 
                      redirectTo="/login_school"
                    />

                    {/* CIE Routes */}
                    <ProtectedRoute 
                      exact 
                      path="/cie_dashboard" 
                      component={CieValidationListPage} 
                      redirectTo="/login_cie"
                    />
                    
                    <ProtectedRoute 
                      exact 
                      path="/cie_validation-list" 
                      component={CieValidationListPage} 
                      redirectTo="/login_cie"
                    />

                    {/* Admin Routes */}
                    <ProtectedRoute 
                      exact 
                      path="/student_list" 
                      component={StudentList} 
                      redirectTo="/login_admin"
                    />
                    
                    <ProtectedRoute 
                      exact 
                      path="/lga_student_database" 
                      component={LGAStudentDatabase} 
                      redirectTo="/login_admin"
                    />
                    
                    <ProtectedRoute 
                      exact 
                      path="/school_management" 
                      component={SchoolManagement} 
                      redirectTo="/login_admin"
                    />
                    
                    <ProtectedRoute 
                      exact 
                      path="/management_console" 
                      component={ManagementConsole} 
                      redirectTo="/login_admin"
                    />
                    
                    <ProtectedRoute 
                      exact 
                      path="/admin_students" 
                      component={StudentList} 
                      redirectTo="/login_admin"
                    />

                    <ProtectedRoute 
                      exact 
                      path="/admin_schools" 
                      component={SchoolManagement} 
                      redirectTo="/login_admin"
                    />
                    
                    <ProtectedRoute 
                      exact 
                      path="/add_result" 
                      component={AddResult} 
                      redirectTo="/login_admin"
                    />
                    
                    <ProtectedRoute 
                      exact 
                      path="/admin_management" 
                      component={ManagementConsole} 
                      redirectTo="/login_admin"
                    />
                    
                    <ProtectedRoute 
                      exact 
                      path="/admin_dashboard" 
                      component={AdminDashboard} 
                      redirectTo="/login_admin"
                    />

                    <ProtectedRoute 
                      exact 
                      path="/admin_settings" 
                      component={AdminSettings} 
                      redirectTo="/login_admin"
                    />

                    <ProtectedRoute 
                      exact 
                      path="/photobook" 
                      component={PhotobookPage} 
                      redirectTo="/login_admin"
                    />

                    <ProtectedRoute 
                      exact 
                      path="/admin_stats" 
                      component={ActivityStatsPage} 
                      redirectTo="/login_admin"
                    />

                    {/* <ProtectedRoute 
                      exact 
                      path="/validation_online" 
                      component={ValidationListPage_online} 
                      redirectTo="/login_admin"
                    /> */}

                    {/* Catch-all route for 404 */}
                    <Route render={() => <div>404 - Page Not Found</div>} />
                  </Switch>
                </IonRouterOutlet>
              </IonSplitPane>
            )}
          </IonReactRouter>
          <ToastContainer />
        </AuthContext.Provider>
      </LocalizationProvider>
    </IonApp>
  );
};

export default App;